import { useSanitizedRouteParams } from 'src/hooks/useSanitizedRouteParams';
import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';

import { Form } from 'antd';
import { FormProvider, Resolver, useForm, UseFormReturn } from 'react-hook-form';

import {
  FanguardApiContractsClientCampaignDetailModel,
  FanguardApiContractsClientEmailTemplateModel,
  FanguardApiContractsClientSiteDetailModel,
} from 'src/api/generated';
import { useGetEmailTemplates } from 'src/hooks/api/useEmailTemplateEndpoint';
import { useGetCampaign, useGetSite, useUpdateCampaign } from 'src/hooks/api/useSiteEndpoint';

import { yupResolver } from '@hookform/resolvers/yup';

import { campaignSchema } from './config';

interface FormValues {
  [key: string]: string | null | boolean | object | number | undefined;
  pathSegment?: string;
  tagline?: string;
  program: string | null;
  connectionId: string | null;
  walletConnectionId: string | null;
  requireId: boolean;
  requiredMinimumAge: string | null;
  venueId: string | null;
  listId: string | null;
  emailVerificationTemplateId: string | null;
  registrationCompleteTemplateId: string | null;
  registrationMissingPhotosTemplateId: string | null;
  noPhotoEmailReminderDelay: number | null;
  enrollmentByRequestOnly: boolean;
  requestEnrollmentTemplateId: string | null;
  registrationIncompleteTemplateId: string | null;
  content: {
    introduction: {
      enabled: boolean;
      marketingInfo: string;
    };
    registration: {
      confirmation: string;
      callToActionButtonTitle: string;
      callToActionButtonLink: string;
    };
    photoUpload: {
      instructions: string;
      success: string;
    };
  };
}

interface CampaignDetailsContextType {
  formMethods: UseFormReturn<FormValues>;
  campaign: FanguardApiContractsClientCampaignDetailModel | undefined;
  isLoading: boolean;
  site: FanguardApiContractsClientSiteDetailModel | undefined;
  isSiteLoading: boolean;
  siteError: Error | null;
  isUpdatingCampaign: boolean;
  handleSave: () => void;
  emailTemplates: FanguardApiContractsClientEmailTemplateModel[];
  isEmailTemplatesLoading: boolean;
  handleCancel: () => void;
}

const CampaignDetailsContext = createContext<CampaignDetailsContextType | undefined>(undefined);

export const useCampaignDetails = () => {
  const context = useContext(CampaignDetailsContext);
  if (!context) {
    throw new Error('useCampaignDetails must be used within a CampaignDetailsProvider');
  }
  return context;
};

interface CampaignDetailsProviderProps {
  children: ReactNode;
}

export const CampaignDetailsProvider = ({ children }: CampaignDetailsProviderProps) => {
  const { id, campaignId } = useSanitizedRouteParams();

  const { data: campaign, isLoading } = useGetCampaign(id, campaignId);
  const { data: site, isLoading: isSiteLoading, error: siteError } = useGetSite(id);
  const { mutate: updateCampaign, isPending: isUpdatingCampaign } = useUpdateCampaign(id ?? '', campaignId ?? '');
  const { data: emailTemplates, isLoading: isEmailTemplatesLoading } = useGetEmailTemplates();

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(campaignSchema) as Resolver<FormValues>,
    defaultValues: {
      pathSegment: '',
      tagline: '',
      program: '',
      connectionId: '',
      walletConnectionId: '',
      requireId: false,
      requiredMinimumAge: null,
      venueId: '',
      listId: '',
      emailVerificationTemplateId: '',
      registrationCompleteTemplateId: '',
      registrationMissingPhotosTemplateId: '',
      noPhotoEmailReminderDelay: 15,
      enrollmentByRequestOnly: false,
      requestEnrollmentTemplateId: '',
      registrationIncompleteTemplateId: '',
      content: {
        introduction: {
          enabled: false,
          marketingInfo: '',
        },
        registration: {
          confirmation: '',
          callToActionButtonTitle: '',
          callToActionButtonLink: '',
        },
        photoUpload: {
          instructions: '',
          success: '',
        },
      },
    },
  });

  const handleCancel = () => {
    formMethods.reset({
      pathSegment: campaign?.pathSegment ?? '',
      tagline: campaign?.tagline ?? '',
      program: campaign?.program ?? null,
      connectionId: campaign?.connectionId ?? null,
      walletConnectionId: campaign?.walletConnectionId ?? null,
      requireId: campaign?.requireId ?? false,
      requiredMinimumAge: campaign?.requiredMinimumAge?.toString() ?? null,
      venueId: campaign?.venueId ?? null,
      listId: campaign?.listId ?? null,
      emailVerificationTemplateId: campaign?.emailVerificationTemplateId ?? null,
      registrationCompleteTemplateId: campaign?.registrationCompleteTemplateId ?? null,
      registrationMissingPhotosTemplateId: campaign?.registrationMissingPhotosTemplateId ?? null,
      noPhotoEmailReminderDelay: campaign?.noPhotoEmailReminderDelay ?? 0,
      enrollmentByRequestOnly: campaign?.enrollmentByRequestOnly ?? false,
      requestEnrollmentTemplateId: campaign?.requestEnrollmentTemplateId ?? null,
      registrationIncompleteTemplateId: campaign?.registrationIncompleteTemplateId ?? null,
      content: {
        introduction: {
          enabled: campaign?.content?.introduction?.enabled ?? false,
          marketingInfo: campaign?.content?.introduction?.marketingInfo ?? '',
        },
        registration: {
          confirmation: campaign?.content?.registration?.confirmation ?? '',
          callToActionButtonTitle: campaign?.content?.registration?.callToActionButtonTitle ?? '',
          callToActionButtonLink: '',
        },
        photoUpload: {
          instructions: campaign?.content?.photoUpload?.instructions ?? '',
          success: campaign?.content?.photoUpload?.success ?? '',
        },
      },
    });
  };

  const handleSave = async () => {
    const isValid = await formMethods.trigger();
    if (isValid && campaign) {
      const formValues = formMethods.getValues();
      const dataToSend = {
        ...formValues,
        id: `${site?.id}`,
        campaignId: `${campaign.id}`,
        requiredMinimumAge: formValues.requiredMinimumAge ? Number(formValues.requiredMinimumAge) : null,
      };
      updateCampaign(dataToSend);
    }
  };

  useEffect(() => {
    formMethods.reset({
      pathSegment: campaign?.pathSegment ?? '',
      tagline: campaign?.tagline ?? '',
      program: campaign?.program ?? null,
      connectionId: campaign?.connectionId ?? null,
      walletConnectionId: campaign?.walletConnectionId ?? null,
      requireId: campaign?.requireId ?? false,
      requiredMinimumAge: campaign?.requiredMinimumAge?.toString() ?? null,
      venueId: campaign?.venueId ?? null,
      listId: campaign?.listId ?? null,
      emailVerificationTemplateId: campaign?.emailVerificationTemplateId ?? null,
      registrationCompleteTemplateId: campaign?.registrationCompleteTemplateId ?? null,
      registrationMissingPhotosTemplateId: campaign?.registrationMissingPhotosTemplateId ?? null,
      noPhotoEmailReminderDelay: campaign?.noPhotoEmailReminderDelay ?? 15,
      enrollmentByRequestOnly: campaign?.enrollmentByRequestOnly ?? false,
      requestEnrollmentTemplateId: campaign?.requestEnrollmentTemplateId ?? null,
      registrationIncompleteTemplateId: campaign?.registrationIncompleteTemplateId ?? null,
      content: {
        introduction: {
          enabled: campaign?.content?.introduction?.enabled ?? false,
          marketingInfo: campaign?.content?.introduction?.marketingInfo ?? '',
        },
        registration: {
          confirmation: campaign?.content?.registration?.confirmation ?? '',
          callToActionButtonTitle: campaign?.content?.registration?.callToActionButtonTitle ?? '',
          callToActionButtonLink: '',
        },
        photoUpload: {
          instructions: campaign?.content?.photoUpload?.instructions ?? '',
          success: campaign?.content?.photoUpload?.success ?? '',
        },
      },
    });
  }, [campaign, formMethods]);

  const value = useMemo(
    () => ({
      formMethods,
      campaign,
      isLoading,
      site,
      isSiteLoading,
      siteError,
      isUpdatingCampaign,
      handleSave,
      emailTemplates: emailTemplates ?? [],
      isEmailTemplatesLoading,
      handleCancel,
    }),
    [
      formMethods,
      campaign,
      isLoading,
      site,
      isSiteLoading,
      siteError,
      isUpdatingCampaign,
      handleSave,
      emailTemplates,
      isEmailTemplatesLoading,
      handleCancel,
    ],
  );

  return (
    <CampaignDetailsContext.Provider value={value}>
      <Form layout="vertical" data-testid="campaign-details-form-provider">
        <FormProvider {...formMethods}>{children}</FormProvider>
      </Form>
    </CampaignDetailsContext.Provider>
  );
};
